import { useState, useMemo } from 'react';
import { useGetEmployeesQuery } from '@/services/api/user/employeeApi';
import { useGetTeamsQuery } from '@/services/api/team/teamApi';
import { useGetLocationsQuery } from '@/services/api/location/locationApi';
import { useGetPermissionQuery } from '@/services/api/permissions/permissionApi';
import { useGetRolePermissionQuery } from '@/services/api/rolePermission/rolePermissionApi';
import { 
  extractApiData,
  extractPaginationInfo,
  transformEmployeeData, 
  transformRoleData, 
  transformStaffCountData, 
  transformRoleCountData, 
  transformPermissionData,
  transformPermissionCountData // Add this
} from '../utils/dataUtils';

/**
 * Custom hook for managing staff-related data
 * Implements Single Responsibility Principle by focusing only on data fetching and transformation
 * Now includes server-side pagination support
 */
export const useStaffData = () => {
  // ==========================================
  // Pagination State for Each Tab
  // ==========================================
  const [employeesPagination, setEmployeesPagination] = useState({
    page: 1,
    per_page: 10,
  });

  const [rolesPagination, setRolesPagination] = useState({
    page: 1,
    per_page: 10,
  });

  const [permissionsPagination, setPermissionsPagination] = useState({
    page: 1,
    per_page: 10,
  });

  // ==========================================
  // Search State for Each Tab
  // ==========================================
  const [employeesSearch, setEmployeesSearch] = useState("");
  const [rolesSearch, setRolesSearch] = useState("");
  const [permissionsSearch, setPermissionsSearch] = useState("");

  // ==========================================
  // API Queries with Pagination and Search
  // ==========================================
  const employeesQuery = useGetEmployeesQuery({
    page: employeesPagination.page,
    per_page: employeesPagination.per_page,
    search: employeesSearch,
  });

  const rolesQuery = useGetRolePermissionQuery({
    page: rolesPagination.page,
    per_page: rolesPagination.per_page,
    search: rolesSearch,
  });

  const permissionsQuery = useGetPermissionQuery({
    page: permissionsPagination.page,
    per_page: permissionsPagination.per_page,
    search: permissionsSearch,
  });

  // Non-paginated queries (if teams/locations don't need pagination)
  const teamsQuery = useGetTeamsQuery();
  const locationsQuery = useGetLocationsQuery();

  // ==========================================
  // Extract Raw Data (from nested response)
  // ==========================================
  const rawEmployeeData = extractApiData(employeesQuery.data);
  const rawRolesData = extractApiData(rolesQuery.data);
  const rawTeamsData = extractApiData(teamsQuery.data);
  const rawLocationsData = extractApiData(locationsQuery.data);
  const rawPermissionsData = extractApiData(permissionsQuery.data);

  // ==========================================
  // Transform Data for Display
  // ==========================================
  const employeeData = useMemo(() => 
    transformEmployeeData(rawEmployeeData), [rawEmployeeData]);
  
  const rolesData = useMemo(() => 
    transformRoleData(rawRolesData), [rawRolesData]);
  
  const permissionsData = useMemo(() => 
    transformPermissionData(rawPermissionsData), [rawPermissionsData]);
  
  const staffCountData = transformStaffCountData(employeesQuery.data);
  const roleCountData = transformRoleCountData(rawRolesData);
  const permissionsCountData = transformPermissionCountData(rawPermissionsData); // Add this

  // ==========================================
  // Extract Pagination Info from API Response
  // (Using extractPaginationInfo from dataUtils)
  // ==========================================
  const employeesPaginationInfo = useMemo(() => 
    extractPaginationInfo(employeesQuery.data), [employeesQuery.data]);

  const rolesPaginationInfo = useMemo(() => 
    extractPaginationInfo(rolesQuery.data), [rolesQuery.data]);

  const permissionsPaginationInfo = useMemo(() => 
    extractPaginationInfo(permissionsQuery.data), [permissionsQuery.data]);

  // ==========================================
  // Pagination Handlers
  // ==========================================
  // Employees
  const handleEmployeesPageChange = (page) => {
    setEmployeesPagination(prev => ({ ...prev, page }));
  };

  const handleEmployeesPageSizeChange = (per_page) => {
    setEmployeesPagination({ page: 1, per_page }); // Reset to page 1
  };

  // Roles
  const handleRolesPageChange = (page) => {
    setRolesPagination(prev => ({ ...prev, page }));
  };

  const handleRolesPageSizeChange = (per_page) => {
    setRolesPagination({ page: 1, per_page });
  };

  // Permissions
  const handlePermissionsPageChange = (page) => {
    setPermissionsPagination(prev => ({ ...prev, page }));
  };

  const handlePermissionsPageSizeChange = (per_page) => {
    setPermissionsPagination({ page: 1, per_page });
  };

  // ==========================================
  // Search Handlers
  // ==========================================
  // Employees
  const handleEmployeesSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setEmployeesSearch(trimmedSearch);
    setEmployeesPagination(prev => ({ ...prev, page: 1 }));
  };

  // Roles
  const handleRolesSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setRolesSearch(trimmedSearch);
    setRolesPagination(prev => ({ ...prev, page: 1 }));
  };

  // Permissions
  const handlePermissionsSearch = (searchTerm) => {
    const trimmedSearch = searchTerm.trim();
    setPermissionsSearch(trimmedSearch);
    setPermissionsPagination(prev => ({ ...prev, page: 1 }));
  };

  // ==========================================
  // Return All Data
  // ==========================================
  return {
    // Raw data
    rawEmployeeData,
    rawRolesData,
    rawTeamsData,
    rawLocationsData,
    rawPermissionsData,
    
    // Transformed data
    employeeData,
    rolesData,
    permissionsData,
    staffCountData,
    roleCountData,
    permissionsCountData, // Add this

    // Pagination info
    employeesPaginationInfo,
    rolesPaginationInfo,
    permissionsPaginationInfo,

    // Pagination handlers
    handleEmployeesPageChange,
    handleEmployeesPageSizeChange,
    handleRolesPageChange,
    handleRolesPageSizeChange,
    handlePermissionsPageChange,
    handlePermissionsPageSizeChange,

    // Search handlers
    handleEmployeesSearch,
    handleRolesSearch,
    handlePermissionsSearch,

    // Search state (optional - for display or debugging)
    employeesSearch,
    rolesSearch,
    permissionsSearch,
    
    // Loading states
    isLoadingEmployees: employeesQuery.isLoading,
    isLoadingRoles: rolesQuery.isLoading,
    isLoadingTeams: teamsQuery.isLoading,
    isLoadingLocations: locationsQuery.isLoading,
    isLoadingPermissions: permissionsQuery.isLoading,
    
    // Fetching states
    isFetchingEmployees: employeesQuery.isFetching,
    isFetchingRoles: rolesQuery.isFetching,
    isFetchingTeams: teamsQuery.isFetching,
    isFetchingLocations: locationsQuery.isFetching,
    isFetchingPermissions: permissionsQuery.isFetching,
    
    // Error states
    employeesError: employeesQuery.error,
    rolesError: rolesQuery.error,
    teamsError: teamsQuery.error,
    locationsError: locationsQuery.error,
    permissionsError: permissionsQuery.error,
    
    // Error flags
    isEmployeesError: employeesQuery.isError,
    isRolesError: rolesQuery.isError,
    isTeamsError: teamsQuery.isError,
    isLocationsError: locationsQuery.isError,
    isPermissionsError: permissionsQuery.isError,
  };
};